/**
 * EngineTouchControlAdapter — production bridge between touch UI and InputSystem.
 *
 * Implements both TouchControlAdapter (consumed by JoystickPad and AbilityPad)
 * and VirtualInputSource (polled by InputSystem.generateCommand).
 *
 * Coordinate convention for movement vector is screen-space:
 *   x: -1 (left)  →  +1 (right)
 *   y: -1 (up)    →  +1 (down)
 *
 * Thresholds:
 *   - Horizontal deadzone: |x| > 0.20
 *   - Vertical threshold (ladders/climbing): |y| > 0.35
 *   - Upward jump threshold: y < -0.45
 */

import { InputModalityStore } from '../../systems/input/InputModalityStore';
import type {
  VirtualInputSource,
  VirtualInputState,
} from '../../systems/input/virtualInputSource';
import type {
  TouchButtonPhase,
  TouchControlAdapter,
  TouchMoveIntent,
} from './types';

const HORIZONTAL_DEADZONE = 0.20;
const VERTICAL_DEADZONE = 0.35;
const JUMP_UP_THRESHOLD = -0.45;

export class EngineTouchControlAdapter implements TouchControlAdapter, VirtualInputSource {
  public readonly id = 'engine-touch-adapter';

  private moveX = 0;
  private moveY = 0;
  private pendingAttack = false;
  private pendingSecondaryAttack = false;
  private pendingAbilitySlot: number | undefined = undefined;
  private pendingJump = false;

  // ── TouchControlAdapter Implementation ──────────────────────────────────

  public onMove(intent: TouchMoveIntent): void {
    InputModalityStore.noteTouchActivity();
    this.moveX = Math.max(-1, Math.min(1, intent.x));
    this.moveY = Math.max(-1, Math.min(1, intent.y));
  }

  public onMoveEnd(): void {
    this.moveX = 0;
    this.moveY = 0;
  }

  public onAbility(slot: number, phase: TouchButtonPhase): void {
    InputModalityStore.noteTouchActivity();
    if (phase === 'press') {
      this.pendingAbilitySlot = slot;
    }
  }

  public onAttack(phase: TouchButtonPhase): void {
    InputModalityStore.noteTouchActivity();
    if (phase === 'press') {
      this.pendingAttack = true;
    }
  }

  public onJumpPress(): void {
    InputModalityStore.noteTouchActivity();
    this.pendingJump = true;
  }

  // ── VirtualInputSource Implementation ───────────────────────────────────

  public poll(): VirtualInputState | null {
    const left = this.moveX < -HORIZONTAL_DEADZONE;
    const right = this.moveX > HORIZONTAL_DEADZONE;
    const up = this.moveY < -VERTICAL_DEADZONE;
    const down = this.moveY > VERTICAL_DEADZONE;
    const jump = this.pendingJump || this.moveY < JUMP_UP_THRESHOLD;

    const attack = this.pendingAttack;
    const secondaryAttack = this.pendingSecondaryAttack;
    const abilitySlot = this.pendingAbilitySlot;

    // Reset discrete edges on poll
    this.pendingAttack = false;
    this.pendingSecondaryAttack = false;
    this.pendingAbilitySlot = undefined;
    this.pendingJump = false;

    const hasActiveInput =
      left || right || up || down || jump || attack || secondaryAttack || abilitySlot !== undefined;

    if (!hasActiveInput) {
      return null;
    }

    return {
      left,
      right,
      up,
      down,
      jump,
      attack,
      secondaryAttack,
      abilitySlot,
    };
  }

  public clear(): void {
    this.moveX = 0;
    this.moveY = 0;
    this.pendingAttack = false;
    this.pendingSecondaryAttack = false;
    this.pendingAbilitySlot = undefined;
    this.pendingJump = false;
  }
}
